import type {
  MiningIneligibleReason,
  MiningPlanet,
  MiningShipOption,
  MiningState,
} from '@kenocyber/shared';
import { ORE_MICRO_PER_UNIT } from '@kenocyber/shared';
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * The mining window.
 *
 * ## ONE window
 *
 * A backdrop, a panel, a close button and `Escape` - the same shape every other
 * modal in the game has, and larger than all of them because it has a stage in
 * it. It was two things before: a floating panel of controls and, separately, a
 * ring drawn across the whole arena behind it, which is two windows for one
 * feature and gave the player no way to tell what the ring belonged to.
 *
 * ## The stage is a hole, not a second canvas
 *
 * `.mining-stage` renders nothing. It is a transparent rectangle whose measured
 * position is handed to the Phaser scene, which draws the planet and the rings
 * through it on the arena's own canvas. That is what keeps the whole feature at
 * one canvas, one renderer and one WebGL context - a canvas parented to this
 * modal would be a second context created and destroyed on every open.
 *
 * ## Nothing here is computed
 *
 * Power, production, stored ore, the cap, the planet's health and its tier all
 * arrive from the server and are printed. The only arithmetic in this file
 * turns micro-units into a readable figure, for DISPLAY. A panel that
 * recalculated any of them would be a panel that could be wrong about what a
 * player owns.
 *
 * ## No optimistic update
 *
 * Every button hands the action to the hook and waits. While one is in flight
 * they are all disabled: the server serialises them anyway, and a button that
 * still looks pressable is a lie about what the game is doing. A refusal leaves
 * the whole screen exactly as it was and shows the server's own reason.
 */

/** The reasons, in the player's language. Every one is safe to show. */
const REASON_LABELS: Record<MiningIneligibleReason, string> = {
  equipped: 'Equipada',
  mining: 'Minerando',
  reserved: 'Reservada',
  unavailable: 'Indisponível',
};

const RARITY_LABELS: Record<string, string> = {
  common: 'Comum',
  uncommon: 'Incomum',
  rare: 'Rara',
  epic: 'Épica',
  mythic: 'Mítica',
  legendary: 'Lendária',
  excellent: 'Excelente',
  perfect: 'Perfeita',
  anomaly: 'Anomalia',
  prismatic: 'Prismática',
};

/** What a planet's tier is called on screen. Size, in the player's words. */
const TIER_LABELS: Record<number, string> = {
  1: 'Fragmento',
  2: 'Asteroide',
  3: 'Planetoide',
  4: 'Planeta',
  5: 'Gigante',
  6: 'Colosso',
};

/**
 * Whole ore, to `decimals` places. The only division in the file.
 *
 * The stored truth is micro-units and stays that way everywhere else; this
 * divides for DISPLAY alone.
 *
 * The rate needs more places than a balance does: at the provisional
 * coefficient a small fleet makes thousandths of a unit a second, and two
 * decimals printed that as "0,00/s" - true, and useless to read.
 */
function formatOre(micro: string, decimals = 2): string {
  const value = BigInt(micro);
  const scale = 10n ** BigInt(decimals);
  const whole = value / ORE_MICRO_PER_UNIT;
  const fraction = ((value % ORE_MICRO_PER_UNIT) * scale) / ORE_MICRO_PER_UNIT;
  return `${whole.toString(10)},${fraction.toString(10).padStart(decimals, '0')}`;
}

/** Whole numbers with thousands separators. Never a float, never a Number. */
function formatWhole(value: string): string {
  const digits = BigInt(value).toString(10);
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/** How long ago the server last settled. Read from ITS instant, not a clock. */
function sinceLabel(lastSettledAt: string | null): string {
  if (lastSettledAt === null) {
    return '—';
  }
  const elapsedMs = Date.now() - new Date(lastSettledAt).getTime();
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0) {
    return 'agora';
  }
  const seconds = Math.floor(elapsedMs / 1000);
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  return minutes < 60 ? `${minutes}min` : `${Math.floor(minutes / 60)}h`;
}

/**
 * A countdown, as `mm:ss` - or `h:mm:ss` once it passes an hour.
 *
 * The hour form is not a second format so much as the same one refusing to
 * print "412:07": a tier 6 against a small fleet is measured in hours, and a
 * minute field in the hundreds is a number nobody reads as a time.
 */
export function countdownLabel(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(whole / 60) % 60;
  const secs = whole % 60;
  const hours = Math.floor(whole / 3_600);
  const mmss = `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  return hours > 0 ? `${hours}:${mmss}` : mmss;
}

/**
 * What the health looks like RIGHT NOW, projected from the last answer.
 *
 * The server owns the health: it moves by `Power x seconds` inside the
 * settlement and nowhere else. But the panel reads once when it opens and never
 * polls, so without this the bar would sit still for as long as the window is
 * open and then jump on the next read - which reads as a broken screen rather
 * than as a planet being mined.
 *
 * So this counts DOWN from the last authoritative figure at the authoritative
 * rate, and every new answer resets it. It writes nothing, confirms nothing and
 * is never sent anywhere: a divergence is corrected the moment the server
 * speaks, and the projection can only ever be a guess about what the server
 * will say next.
 *
 * Bigints, because the health is one: at a hundred Power a projection held as a
 * double would start disagreeing with the server's own arithmetic within a day.
 */
function useProjectedHealth(planet: MiningPlanet | null): bigint {
  const authoritative = planet ? BigInt(planet.remainingHealth) : 0n;
  const perSecond = planet ? BigInt(planet.damagePerSecond) : 0n;
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    setElapsedMs(0);
    if (perSecond <= 0n || authoritative <= 0n) {
      // Nothing is being mined, so nothing is projected. A ticking bar with no
      // fleet behind it would be an invention.
      return;
    }
    const startedAt = Date.now();
    const timer = window.setInterval(() => setElapsedMs(Date.now() - startedAt), 250);
    return () => window.clearInterval(timer);
    /*
     * The ANSWER is the dependency, and these two values are the answer.
     *
     * Bigints compare by value under `Object.is`, so a re-render that produced
     * an equal-but-new planet object does not restart the countdown - which it
     * would if the object itself were the dependency, and the bar would never
     * move.
     */
  }, [authoritative, perSecond]);

  if (perSecond <= 0n) {
    return authoritative;
  }
  const removed = (perSecond * BigInt(Math.floor(elapsedMs))) / 1_000n;
  return removed >= authoritative ? 0n : authoritative - removed;
}

export interface MiningStageRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface MiningPanelProps {
  readonly state: MiningState | null;
  readonly loading: boolean;
  readonly busy: boolean;
  readonly error: string | null;
  readonly notice: string | null;
  readonly onAssign: (instanceId: string) => void;
  readonly onRemove: (instanceId: string) => void;
  readonly onCollect: () => void;
  readonly onCancel: () => void;
  /** Told which ship the player highlighted, so the rings can match. */
  readonly onSelect: (instanceId: string | null) => void;
  readonly onClose: () => void;
  /**
   * Where the stage ended up, in canvas pixels.
   *
   * Reported on mount and on every resize. The scene draws through the hole,
   * so if this were wrong the planet would be drawn behind the controls - which
   * is exactly what a fixed guess at the layout would eventually do.
   */
  readonly onStage?: (rect: MiningStageRect) => void;
}

/**
 * How tall one row is, in pixels. Mirrors `--mining-row-height` in the CSS.
 *
 * A constant rather than a measurement: windowing needs to know where row N is
 * WITHOUT mounting it, and a measured height is only available after the mount
 * it is meant to avoid. The stylesheet pins the row to this, and a test asserts
 * the two agree.
 */
export const MINING_ROW_HEIGHT = 44;

/** Rows kept mounted above and below the viewport, so scrolling never flashes. */
const OVERSCAN = 4;

/**
 * A list that mounts only the rows you can see.
 *
 * A hundred ships meant a hundred rows, each with an image, five spans and a
 * button - measured at 1042 DOM nodes against 82 with the window shut, and it
 * was the same 1042 whether one ship was docked or a hundred, because the spare
 * hulls are listed too. The DATA is still whole: every ship is in the array,
 * the scrollbar is the full height, and REMOVER works on a row the moment it
 * scrolls into view. Only the mounting is bounded.
 */
function VirtualShipList({
  ships,
  renderRow,
}: {
  ships: MiningShipOption[];
  renderRow: (ship: MiningShipOption) => ReactNode;
}) {
  const [scrollTop, setScrollTop] = useState(0);
  const [viewport, setViewport] = useState(0);
  const ref = useRef<HTMLUListElement | null>(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }
    const measure = () => setViewport(element.clientHeight);
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(element);
    return () => observer?.disconnect();
  }, []);

  /*
   * A short list is not windowed at all.
   *
   * Below the overscan the arithmetic costs more than the rows do, and the
   * spacer divs would be the only thing it added.
   */
  const windowed = ships.length > OVERSCAN * 3 && viewport > 0;
  const first = windowed ? Math.max(0, Math.floor(scrollTop / MINING_ROW_HEIGHT) - OVERSCAN) : 0;
  const visibleCount = windowed
    ? Math.ceil(viewport / MINING_ROW_HEIGHT) + OVERSCAN * 2
    : ships.length;
  const last = Math.min(ships.length, first + visibleCount);
  const slice = windowed ? ships.slice(first, last) : ships;

  return (
    <ul
      className="mining-list"
      ref={ref}
      data-windowed={windowed ? 'true' : 'false'}
      data-total={ships.length}
      onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
    >
      {/* Spacers carry the rows that are not mounted, so the scrollbar
          describes the whole fleet rather than the visible part of it. */}
      {first > 0 ? <li style={{ height: first * MINING_ROW_HEIGHT }} aria-hidden="true" /> : null}
      {slice.map((ship) => renderRow(ship))}
      {last < ships.length ? (
        <li style={{ height: (ships.length - last) * MINING_ROW_HEIGHT }} aria-hidden="true" />
      ) : null}
    </ul>
  );
}

function ShipRow({
  ship,
  busy,
  selected,
  action,
  onSelect,
}: {
  ship: MiningShipOption;
  busy: boolean;
  selected: boolean;
  action: { label: string; testId: string; run: () => void } | null;
  onSelect: () => void;
}) {
  return (
    <li
      className={`mining-ship${selected ? ' is-selected' : ''}`}
      data-testid={`mining-ship-${ship.instanceId}`}
      data-eligible={ship.eligible ? 'true' : 'false'}
      data-assigned={ship.assigned ? 'true' : 'false'}
    >
      <button
        type="button"
        className="mining-ship__pick"
        data-testid={`mining-select-${ship.instanceId}`}
        onClick={onSelect}
      >
        {/* The thumbnail the vault already ships. No new art. */}
        <img
          className="mining-ship__art"
          src={`/game-assets/ships/${ship.assetKey}.png`}
          alt=""
          width={32}
          height={32}
        />
        <span className="mining-ship__name">{ship.name}</span>
      </button>
      <span className="mining-ship__rarity">{RARITY_LABELS[ship.rarity] ?? ship.rarity}</span>
      <span className="mining-ship__level">Nv {ship.level}</span>
      <span className="mining-ship__power" data-testid={`mining-power-${ship.instanceId}`}>
        {ship.power} Power
      </span>
      <span className="mining-ship__state" data-testid={`mining-state-${ship.instanceId}`}>
        {ship.assigned
          ? 'Minerando'
          : ship.eligible
            ? 'Disponível'
            : (REASON_LABELS[ship.reason ?? 'unavailable'] ?? 'Indisponível')}
      </span>
      {action ? (
        <button type="button" data-testid={action.testId} disabled={busy} onClick={action.run}>
          {action.label}
        </button>
      ) : (
        <span className="mining-ship__blocked">—</span>
      )}
    </li>
  );
}

export function MiningPanel({
  state,
  loading,
  busy,
  error,
  notice,
  onAssign,
  onRemove,
  onCollect,
  onCancel,
  onSelect,
  onClose,
  onStage,
}: MiningPanelProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);

  const select = (instanceId: string) => {
    const next = selected === instanceId ? null : instanceId;
    setSelected(next);
    onSelect(next);
  };

  /*
   * `Escape` closes, exactly like every other panel.
   *
   * On `window`, not the panel, so it answers wherever the focus is - a modal
   * that only closes when its own markup happens to hold focus is a modal that
   * sometimes does not close.
   */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  /**
   * Measures the stage against the CANVAS, not the viewport.
   *
   * The scene works in canvas pixels, so the answer has to be relative to the
   * canvas element's own top-left. Measuring against the window instead would
   * be right only while the canvas happened to start at the top of the page.
   */
  const measure = useCallback(() => {
    const stage = stageRef.current;
    const canvas = document.querySelector('canvas');
    if (!stage || !canvas || !onStage) {
      return;
    }
    const stageBox = stage.getBoundingClientRect();
    const canvasBox = canvas.getBoundingClientRect();
    onStage({
      x: stageBox.left - canvasBox.left,
      y: stageBox.top - canvasBox.top,
      width: stageBox.width,
      height: stageBox.height,
    });
  }, [onStage]);

  // Before paint, so the first frame the player sees already has the planet in
  // the right place rather than jumping into it.
  useLayoutEffect(() => {
    measure();
    const stage = stageRef.current;
    if (!stage) {
      return;
    }
    /*
     * The observer is the precise signal; the window resize is the fallback.
     *
     * Guarded because `ResizeObserver` is not universal - it is absent in the
     * test environment, and a component that threw where an API is missing
     * would take the whole window down for a measurement it can approximate.
     * Every real browser has it, so the fallback is a safety net rather than
     * the normal path.
     */
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => measure());
    observer?.observe(stage);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure]);

  const assigned = state?.fleet.filter((ship) => ship.assigned) ?? [];
  const eligible = state?.fleet.filter((ship) => ship.eligible && !ship.assigned) ?? [];
  const blocked = state?.fleet.filter((ship) => !ship.eligible && !ship.assigned) ?? [];
  const planet = state?.planet ?? null;
  const projectedHealth = useProjectedHealth(planet);
  const maxHealth = planet ? BigInt(planet.maxHealth) : 0n;
  const damagePerSecond = planet ? BigInt(planet.damagePerSecond) : 0n;
  const destroyed = planet !== null && projectedHealth <= 0n;
  const healthPercent = maxHealth > 0n ? Number((projectedHealth * 1_000n) / maxHealth) / 10 : 0;
  /*
   * `vidaRestante / danoPorSegundo`, the same division the server does.
   *
   * Recomputed from the PROJECTED health so the counter ticks with the bar
   * rather than freezing at whatever the last answer said. The server's own
   * `secondsToDestroy` is what corrects it on the next read.
   */
  const secondsLeft = damagePerSecond > 0n ? Number(projectedHealth / damagePerSecond) : null;

  return (
    /*
     * One backdrop, and it does not close the window.
     *
     * Transparent on purpose: the arena is dimmed inside the canvas, so the
     * stage's own hole stays bright instead of being greyed by an element
     * sitting over it. It carries no click handler either - the button and
     * `Escape` are the ways out, and a bare div that reacts to a mouse is a
     * control a keyboard cannot reach.
     */
    <div className="mining-backdrop" data-testid="mining-backdrop">
      <section className="mining-window" data-testid="mining-panel" aria-label="Mineração">
        <header className="mining-window__header">
          <h2>MINERAÇÃO</h2>
          <button
            type="button"
            className="overview-close"
            data-testid="mining-close"
            aria-label="Fechar mineração"
            onClick={onClose}
          >
            ×
          </button>
        </header>

        {error === null ? null : (
          <p className="trade-error" role="alert" data-testid="mining-error">
            {error}
          </p>
        )}
        {notice === null ? null : (
          <p className="mining-notice" role="status" data-testid="mining-notice">
            {notice}
          </p>
        )}

        {loading && state === null ? (
          <p className="trade-empty" data-testid="mining-loading">
            Carregando a mineração…
          </p>
        ) : state === null ? (
          <p className="trade-empty" data-testid="mining-empty">
            Nada para mostrar.
          </p>
        ) : (
          <div className="mining-body">
            {/*
              The hole. It draws nothing itself - the scene draws the planet and
              the rings through it, on the arena's own canvas.
            */}
            <div className="mining-stage" data-testid="mining-stage" ref={stageRef}>
              {planet === null ? (
                <p className="mining-stage__empty" data-testid="mining-no-planet">
                  Nenhum planeta em exploração.
                </p>
              ) : (
                <div className="mining-stage__readout" data-testid="mining-planet">
                  <span className="mining-stage__tier" data-testid="mining-planet-tier">
                    {TIER_LABELS[planet.tier] ?? `Tier ${planet.tier}`} · Tier {planet.tier}
                  </span>
                  <span data-testid="mining-planet-health">
                    Vida do planeta: {formatWhole(projectedHealth.toString(10))} /{' '}
                    {formatWhole(planet.maxHealth)}
                  </span>
                  <div
                    className="mining-stage__bar"
                    data-testid="mining-health-bar"
                    data-percent={healthPercent.toFixed(1)}
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(healthPercent)}
                  >
                    <span style={{ width: `${healthPercent}%` }} />
                  </div>
                  <span data-testid="mining-eta-stage">
                    {destroyed
                      ? 'Destruído'
                      : secondsLeft === null
                        ? 'Aguardando naves'
                        : `Destruição estimada em: ${countdownLabel(secondsLeft)}`}
                  </span>
                </div>
              )}
            </div>

            <div className="mining-side">
              <dl className="mining-summary">
                <div>
                  <dt>Power total</dt>
                  <dd data-testid="mining-total-power">{state.totalPower}</dd>
                </div>
                <div>
                  <dt>Produção</dt>
                  <dd data-testid="mining-rate">{formatOre(state.oreMicroPerSecond, 4)}/s</dd>
                </div>
                <div>
                  <dt>Acumulado</dt>
                  <dd data-testid="mining-stored">{formatOre(state.storedOreMicro)}</dd>
                </div>
                <div>
                  <dt>Limite</dt>
                  <dd data-testid="mining-capacity">{formatOre(state.capacityMicro)}</dd>
                </div>
                <div>
                  <dt>Última liquidação</dt>
                  <dd data-testid="mining-since">{sinceLabel(state.lastSettledAt)}</dd>
                </div>
                <div>
                  <dt>Dano/s</dt>
                  <dd data-testid="mining-damage">{planet ? planet.damagePerSecond : '0'}</dd>
                </div>
                <div>
                  <dt>Destruição estimada em</dt>
                  {/*
                    The same three states as the stage readout, from the same
                    projection - so the two can never disagree about whether a
                    planet is standing.
                  */}
                  <dd data-testid="mining-eta">
                    {destroyed
                      ? 'Destruído'
                      : secondsLeft === null
                        ? 'Aguardando naves'
                        : countdownLabel(secondsLeft)}
                  </dd>
                </div>
                <div>
                  <dt>Recompensa</dt>
                  {/*
                    What a destruction GUARANTEES, and how many independent
                    rolls it makes. Deliberately not the roll itself: naming the
                    rare drop before the planet breaks turns a reward into a
                    countdown, and the odds are the same at every tier anyway.
                  */}
                  <dd data-testid="mining-reward">
                    {planet
                      ? `${formatWhole(planet.potentialGold)} Gold + ${planet.dropPositions} sorteio(s)`
                      : '—'}
                  </dd>
                </div>
              </dl>

              <div className="mining-actions">
                <button
                  type="button"
                  data-testid="mining-collect"
                  disabled={busy || BigInt(state.storedOreMicro) <= 0n}
                  onClick={onCollect}
                >
                  COLETAR
                </button>
                <button
                  type="button"
                  data-testid="mining-cancel"
                  disabled={busy || state.state !== 'active'}
                  onClick={onCancel}
                >
                  CANCELAR MINERAÇÃO
                </button>
              </div>

              <div className="mining-lists">
                <section className="mining-group" data-testid="mining-assigned">
                  <h3>
                    Minerando ({assigned.length}/{state.maxShips})
                  </h3>
                  {/*
                    An empty ring says WHY it is empty.

                    A heading over nothing is the state that reads as a broken
                    screen: the planet is drawn either way, and without a
                    sentence here the player cannot tell "no ships docked" from
                    "this did not load".
                  */}
                  {assigned.length === 0 ? (
                    <p className="trade-empty" data-testid="mining-none-assigned">
                      Nenhuma nave atracada. Designe uma nave para começar a minerar.
                    </p>
                  ) : null}
                  <VirtualShipList
                    ships={assigned}
                    renderRow={(ship) => (
                      <ShipRow
                        key={ship.instanceId}
                        ship={ship}
                        busy={busy}
                        selected={selected === ship.instanceId}
                        onSelect={() => select(ship.instanceId)}
                        action={{
                          label: 'REMOVER',
                          testId: `mining-remove-${ship.instanceId}`,
                          run: () => onRemove(ship.instanceId),
                        }}
                      />
                    )}
                  />
                </section>

                <section className="mining-group" data-testid="mining-eligible">
                  <h3>Disponíveis</h3>
                  {eligible.length === 0 ? (
                    <p className="trade-empty" data-testid="mining-none-eligible">
                      Nenhuma nave disponível: a nave equipada não minera, e as reservadas voltam
                      quando a operação terminar.
                    </p>
                  ) : null}
                  <VirtualShipList
                    ships={eligible}
                    renderRow={(ship) => (
                      <ShipRow
                        key={ship.instanceId}
                        ship={ship}
                        busy={busy}
                        selected={selected === ship.instanceId}
                        onSelect={() => select(ship.instanceId)}
                        action={{
                          label: 'DESIGNAR',
                          testId: `mining-assign-${ship.instanceId}`,
                          run: () => onAssign(ship.instanceId),
                        }}
                      />
                    )}
                  />
                </section>

                <section className="mining-group" data-testid="mining-blocked">
                  <h3>Indisponíveis</h3>
                  <VirtualShipList
                    ships={blocked}
                    renderRow={(ship) => (
                      <ShipRow
                        key={ship.instanceId}
                        ship={ship}
                        busy={busy}
                        selected={selected === ship.instanceId}
                        onSelect={() => select(ship.instanceId)}
                        action={null}
                      />
                    )}
                  />
                </section>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
